// documentation:
//   in the App.js file:
//     step 1:
//       import component using correct path:
//         import FormidableSessionTimeout from './components/FormidableSessionTimeout.jsx'
//     step 2:
//       add this component:
//         <FormidableSessionTimeout inactivityTime="15000" reactionTime="5000" onSignOut={handleSignOut} />
//       if you don't put inactivityTime or reactionTime, it will default to inactivityTime="1200000" reactionTime="30000"
//       if you don't put maxSessionTime, it will default to maxSessionTime=12600000 (12,600,000 - that's 3 hours in milliseconds)
//         <FormidableSessionTimeout onSignOut={handleSignOut} />
//       inactivityTime [in milliseconds] is the maximum time of inactivity
//       reactionTime [in milliseconds] is the maximum time to react to the popup warning you of the session ending


import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import MagnifiquePopup from './MagnifiquePopup.js';
import { Auth } from 'aws-amplify';

function FormidableSessionTimeout({ inactivityTime = 3600000, reactionTime = 30000, onSignOut, maxSessionTime = 12600000 }) {
    const [timeoutId, setTimeoutId] = useState(null);
    const [maxTimeoutId, setMaxTimeoutId] = useState(null);
    const [refreshCheckId, setRefreshCheckId] = useState(null);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [appearPopupSessionTimeout, setAppearPopupSessionTimeout] = useState(false);
    const [appearPopupMaxSessionTimeout, setAppearPopupMaxSessionTimeout] = useState(false);
    const [countdown, setCountdown] = useState(Math.floor(reactionTime/1000)); // Set initial countdown to 10 seconds

    // Function to reset the timer after user activity
    const handleUserActivity = () => {
        setLastActivity(Date.now());
    };

    const setMaxTimeout = async () => {
        let isAuthenticated = false;
        let accessTokenExpiry = 0;
        try {
            let response = await Auth.currentAuthenticatedUser();
            accessTokenExpiry = response?.signInUserSession?.accessToken?.payload?.exp * 1000 ?? 0;
            isAuthenticated = true; //if they aren't authenticated, it throws an error and will skip this line
        } catch (err) {
            //do nothing
        }
        if (isAuthenticated) {
            setMaxTimeoutId(setTimeout(()=>setAppearPopupMaxSessionTimeout(true), maxSessionTime));

            //auto-refresh, if there's been browser side activity on access token expiry
            let refreshSessionTime = Math.max(accessTokenExpiry - Date.now(), 0);
            if (!refreshCheckId && refreshSessionTime > 0) {
                setRefreshCheckId(setTimeout(autoRefreshSession, refreshSessionTime));
            }
        }
    }

    const autoRefreshSession = async () => {
        //only auto-refresh the session if there's been activity within the inactivity time limit
        if (Date.now() - lastActivity < inactivityTime) {
            let isAuthenticated = false;
            let accessTokenExpiry = 0;
            try {
                let response = await Auth.currentAuthenticatedUser();
                accessTokenExpiry = response?.signInUserSession?.accessToken?.payload?.exp * 1000 ?? 0;
                isAuthenticated = true; //if they aren't authenticated, it throws an error and will skip this line
            } catch (err) {
                //do nothing
            }
            if (isAuthenticated) {
                //auto-refresh, if there's been browser side activity on access token expiry
                let refreshSessionTime = Math.max(accessTokenExpiry - Date.now(), 0);
                if (!refreshCheckId && refreshSessionTime > 0) {
                    setRefreshCheckId(setTimeout(autoRefreshSession, refreshSessionTime));
                }
            }
        } else {
            setRefreshCheckId(null);
        }
    }

    // Effect for managing access token timeouts (both max timeout and early token timeouts when there has been UI activity)
    useEffect(() => {
        if (!maxTimeoutId) {
            setMaxTimeout();
        }
    }, []);
    
    // Effect for managing session timeout
    useEffect(() => {
        // Function to handle timeout
        const checkSession = async () => {
            try {
                await Auth.currentAuthenticatedUser();
                //if they aren't authenticated, it throws an error and will skip this line
                const currentTime = Date.now();
                if (currentTime - lastActivity > (inactivityTime-reactionTime)) {
                    // Session has timed out, show popup
                    setAppearPopupSessionTimeout(true);
                } else {
                    // Session still active, reset timer
                    const remainingTime = inactivityTime - (currentTime - lastActivity);
                    const newTimeoutId = setTimeout(checkSession, remainingTime);
                    setTimeoutId(newTimeoutId);
                }
            } catch (err) {
                //do nothing
            }
        };

        // Start initial timer
        const initialTimeoutId = setTimeout(checkSession, inactivityTime-reactionTime);
        setTimeoutId(initialTimeoutId);

        // Event listeners for user activity
        const events = ['mousemove', 'mousedown', 'keypress', 'touchmove', 'touchstart', 'scroll', 'wheel'];
        events.forEach(event => window.addEventListener(event, handleUserActivity));

        // Cleanup function
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            events.forEach(event => window.removeEventListener(event, handleUserActivity));
        };
    }, [inactivityTime, lastActivity]);

    // Effect for managing countdown
    useEffect(() => {
        let countdownTimer;

        if (appearPopupSessionTimeout) {
            setCountdown(Math.floor(reactionTime/1000)); // Reset countdown to 10 seconds when popup appears
            countdownTimer = setInterval(() => {
                setCountdown(prevCountdown => {
                    if (prevCountdown === 1) {
                        clearInterval(countdownTimer);
                        onSignOut(); // Sign out when countdown reaches 0
                        return 0;
                    }
                    return prevCountdown - 1;
                });
            }, 1000); // Decrease countdown every second
        }

        // Cleanup function for the countdown timer
        return () => {
            if (countdownTimer) clearInterval(countdownTimer);
        };
    }, [appearPopupSessionTimeout, onSignOut]);

    return (
        <>
          <MagnifiquePopup 
            id="signoutConfirmation" 
            trigger={appearPopupSessionTimeout} 
            setTrigger={setAppearPopupSessionTimeout} 
            cancelCallback={onSignOut} 
            header={<FormattedMessage id="message.sessiontimeout.confirmation" />}
            body={<FormattedMessage id="message.sessiontimeout.explanation" values={{countdown: countdown, s: countdown === 1 ? '' : 's', b: (chunks) => <b>{chunks}</b>, p: (chunks) => <p>{chunks}</p>, inactivityTimeInMinutes: Math.floor(inactivityTime / 60000)}} />}
            cancelButton={<FormattedMessage id="message.sessiontimeout.endsessionnow" />} 
            continueButton={<FormattedMessage id="message.sessiontimeout.continuesession" />} 
          />
          <MagnifiquePopup 
              id="maxSignoutNotification" 
              trigger={appearPopupMaxSessionTimeout} 
              setTrigger={setAppearPopupMaxSessionTimeout} 
              header={<FormattedMessage id="message.maxSessiontimeout.header" />}
              body={<FormattedMessage id="message.maxSessiontimeout.explanation" values={{p: (chunks) => <p>{chunks}</p>}} />}
              cancelButton={<FormattedMessage id="button.ok" />} 
              cancelCallback={onSignOut} 
          />
        </>
    );
}

export default FormidableSessionTimeout;